/**
 * Team Inbox — 24h session-window math (UI Spec §7.3 "Session banner").
 *
 * PURE — every function takes `nowMs` explicitly so the Node test runner
 * can pin the clock (edge cases: exactly 24h, expiring mid-session).
 * The live countdown in the SessionBanner component re-invokes these on a
 * timer; the math itself never reads Date.now().
 *
 * Contract (mirrors the server lib/waWorkspace.js WINDOW_MS decision —
 * the server remains the authority on every SEND; this is display only):
 *   - window opens at the LAST INBOUND customer message;
 *   - it stays open for exactly 24h;
 *   - at exactly +24h the window is CLOSED (remaining 0 → templates only;
 *     matches the server's `now - lastInbound < WINDOW_MS` strictness);
 *   - under 2h remaining the banner turns amber as a warning (§7.3).
 */
import type { SessionState, SessionTone } from './types';

export const WINDOW_MS = 24 * 60 * 60 * 1000;

/** Amber-warning threshold — under 2h remaining (UI Spec §7.3). */
export const WARNING_MS = 2 * 60 * 60 * 1000;

/** PURE — "23h 41m" / "41m" / "<1m" countdown text. Never negative. */
export function formatCountdown(remainingMs: number): string {
  const ms = Math.max(0, remainingMs);
  const h = Math.floor(ms / 3_600_000);
  const m = Math.floor((ms % 3_600_000) / 60_000);
  if (h > 0) return `${h}h ${m}m`;
  if (m > 0) return `${m}m`;
  return '<1m';
}

/**
 * PURE — full session state from the last inbound timestamp at `nowMs`.
 * `lastInboundMs == null` (no inbound ever — e.g. an outbound-only thread)
 * → closed: WhatsApp only opens a session on a CUSTOMER message.
 */
export function sessionState(
  lastInboundMs: number | null,
  nowMs: number,
): SessionState {
  if (typeof lastInboundMs !== 'number' || !(lastInboundMs > 0)) {
    return {
      open: false,
      tone: 'closed',
      lastInboundMs: null,
      closesAtMs: null,
      remainingMs: 0,
      label: '⏳ Outside 24h — templates only',
    };
  }
  const closesAtMs = lastInboundMs + WINDOW_MS;
  const remainingMs = closesAtMs - nowMs;
  // Exactly 24h elapsed (remainingMs === 0) → CLOSED. Strict, matching
  // the server's `< WINDOW_MS` send gate — display never claims an open
  // window the server would reject.
  const open = remainingMs > 0;
  const tone: SessionTone = !open
    ? 'closed'
    : remainingMs < WARNING_MS
      ? 'warning'
      : 'open';
  return {
    open,
    tone,
    lastInboundMs,
    closesAtMs,
    remainingMs: Math.max(0, remainingMs),
    label: open
      ? `● Session open · closes in ${formatCountdown(remainingMs)}`
      : '⏳ Outside 24h — templates only',
  };
}

/**
 * PURE — when the banner must next recompute: every minute while open
 * (the countdown shows minutes), and exactly AT the close instant so an
 * open session flips to "templates only" mid-session without a reload.
 * Returns ms-until-next-tick (never < 1s), or null when closed (static).
 */
export function nextTickMs(state: SessionState, nowMs: number): number | null {
  if (!state.open || state.closesAtMs == null) return null;
  const toClose = state.closesAtMs - nowMs;
  const toMinute = 60_000 - (nowMs % 60_000);
  return Math.max(1_000, Math.min(toClose, toMinute));
}
